import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [org, setOrg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => { const { data } = await api.get("/settings"); setOrg(data); })();
  }, []);

  if (!org) return <div className="text-zinc-500 text-sm" data-testid="settings-loading">Loading settings…</div>;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings", {
        name: org.name, logo_url: org.logo_url, methodology_text: org.methodology_text,
        default_target_score: Number(org.default_target_score),
        tier_thresholds: {
          tier1_max: Number(org.tier_thresholds.tier1_max),
          tier2_max: Number(org.tier_thresholds.tier2_max),
          tier3_max: Number(org.tier_thresholds.tier3_max),
          tier4_max: Number(org.tier_thresholds.tier4_max),
        },
        scoring_scale_labels: org.scoring_scale_labels,
        report_branding: org.report_branding,
      });
      setOrg(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="settings-page">
      <div>
        <div className="label-uppercase">Organization Configuration</div>
        <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Configure tier thresholds, scoring scale, branding, and methodology.</p>
      </div>

      <div className="mt-6 space-y-6">
        <Section title="Branding">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase">Organization name</Label>
              <Input value={org.name || ""} onChange={(e) => setOrg({ ...org, name: e.target.value })} data-testid="org-name-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
            </div>
            <div>
              <Label className="label-uppercase">Logo URL</Label>
              <Input value={org.logo_url || ""} onChange={(e) => setOrg({ ...org, logo_url: e.target.value })} data-testid="org-logo-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
            </div>
          </div>
        </Section>

        <Section title="Default Targets">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase">Default target score (1–5)</Label>
              <Input type="number" min={1} max={5} step={0.5} value={org.default_target_score}
                onChange={(e) => setOrg({ ...org, default_target_score: Number(e.target.value) })}
                data-testid="default-target-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800 mono" />
            </div>
          </div>
        </Section>

        <Section title="Tier Thresholds">
          <div className="grid grid-cols-4 gap-3">
            {["tier1_max", "tier2_max", "tier3_max", "tier4_max"].map((k) => (
              <div key={k}>
                <Label className="label-uppercase">{k.replace("_", " ")}</Label>
                <Input type="number" min={0} max={5} step={0.1} value={org.tier_thresholds?.[k] ?? 0}
                  onChange={(e) => setOrg({ ...org, tier_thresholds: { ...org.tier_thresholds, [k]: Number(e.target.value) } })}
                  data-testid={`threshold-${k}`} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 mono" />
              </div>
            ))}
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">CSF Tier mapping. Default: Tier1 ≤ 1.5, Tier2 ≤ 2.5, Tier3 ≤ 3.5, Tier4 ≤ 5.0</div>
        </Section>

        <Section title="Scoring Scale Labels">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <div key={n}>
                <Label className="label-uppercase">Score {n}</Label>
                <Input value={org.scoring_scale_labels?.[String(n)] || ""}
                  onChange={(e) => setOrg({ ...org, scoring_scale_labels: { ...org.scoring_scale_labels, [String(n)]: e.target.value } })}
                  data-testid={`label-${n}`} className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
              </div>
            ))}
          </div>
        </Section>

        <Section title="Methodology">
          <Textarea rows={4} value={org.methodology_text || ""} onChange={(e) => setOrg({ ...org, methodology_text: e.target.value })}
            data-testid="methodology-input" className="bg-[#0A0A0A] border-zinc-800" />
        </Section>

        <Button onClick={save} disabled={busy} data-testid="save-settings-button" className="bg-white text-black hover:bg-zinc-200">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-[#121212] p-5">
      <div className="label-uppercase mb-4">{title}</div>
      {children}
    </div>
  );
}
